import {
  ElectricityUsageRequirementsInput,
  EnergyBasketFragment,
  EnergyFuelType,
  EnergyMeterType,
  EnergyPaymentMethod,
  EnergyPaymentMethodInput,
  EnergyQuoteRequirementsInput,
  GasUsageRequirementsInput,
  PredefinedConsumption,
} from '@generated/gql/graphql';
import { EnergyDetailsFormValues } from '@src/types/energy';
import { BooleanSelection } from '@src/types/index';

import { hasElectricityService, hasGasService } from '../utils';

interface ComposeRequirementsParams {
  basketId: string;
  energy: EnergyBasketFragment;
  submitValues: EnergyDetailsFormValues;
}

interface GetGasInputArgs {
  fuelType: EnergyFuelType;
  gasUsage: string;
  predefinedConsumption: PredefinedConsumption;
}

interface GetElectricityInputArgs {
  fuelType: EnergyFuelType;
  electricityUsageDay: string;
  electricityUsageNight: string;
  meterType: EnergyMeterType;
  predefinedConsumption: PredefinedConsumption;
}

const getEstimatedUsage = (
  predefinedConsumption: PredefinedConsumption,
  usage: string
) =>
  predefinedConsumption === PredefinedConsumption.Custom
    ? Number(usage || 0)
    : undefined;

const getGasInput = ({
  fuelType,
  gasUsage,
  predefinedConsumption,
}: GetGasInputArgs): Nullable<GasUsageRequirementsInput> => {
  if (!hasGasService(fuelType)) {
    return null;
  }

  return {
    estimatedAnnualConsumption: getEstimatedUsage(
      predefinedConsumption,
      gasUsage
    ),
    predefinedConsumption,
  };
};

const getElectricityInput = ({
  fuelType,
  electricityUsageDay,
  electricityUsageNight,
  meterType,
  predefinedConsumption,
}: GetElectricityInputArgs): Nullable<ElectricityUsageRequirementsInput> => {
  if (!hasElectricityService(fuelType)) {
    return null;
  }

  let estimatedAnnualConsumptionRegister1 = getEstimatedUsage(
    predefinedConsumption,
    electricityUsageDay
  );

  // INFO: Missing electricityUsageNight input on purpose
  // https://utilitywarehouse.slack.com/archives/C03L36BQMFC/p1699604765159879
  let estimatedAnnualConsumptionRegister2 = undefined;

  // FIXME: Temporary solution - there will be two inputs eventually
  if (
    predefinedConsumption === PredefinedConsumption.Custom &&
    meterType === EnergyMeterType.Economy_7
  ) {
    estimatedAnnualConsumptionRegister1 = Number(electricityUsageDay);
    estimatedAnnualConsumptionRegister2 = Number(electricityUsageNight);
  }

  return {
    meterType,
    estimatedAnnualConsumptionRegister1,
    estimatedAnnualConsumptionRegister2,
    predefinedConsumption,
  };
};

const getPaymentMethods = (
  hasPrepayment: BooleanSelection,
  prepaymentFuelType: EnergyFuelType,
  fuelType: EnergyFuelType
): EnergyPaymentMethodInput => {
  const isPrepayment = (fuel: EnergyFuelType) =>
    hasPrepayment === BooleanSelection.Yes &&
    [EnergyFuelType.DualFuel, fuel].includes(prepaymentFuelType);

  return {
    electricity:
      hasElectricityService(fuelType) &&
      isPrepayment(EnergyFuelType.ElectricityOnly)
        ? EnergyPaymentMethod.Prepayment
        : EnergyPaymentMethod.DirectDebit,
    gas:
      hasGasService(fuelType) && isPrepayment(EnergyFuelType.GasOnly)
        ? EnergyPaymentMethod.Prepayment
        : EnergyPaymentMethod.DirectDebit,
  };
};

const parseInput = (formValues: EnergyDetailsFormValues) => {
  const {
    electricityUsageDay,
    electricityUsageNight,
    fuelType,
    gasUsage,
    hasPrepayment,
    meterType,
    paymentOption,
    predefinedConsumption,
    prepaymentFuelType,
  } = formValues;

  const paymentMethods = getPaymentMethods(
    hasPrepayment,
    prepaymentFuelType,
    fuelType
  );

  const gasInput = getGasInput({
    gasUsage,
    predefinedConsumption,
    fuelType,
  });

  const electricityInput = getElectricityInput({
    meterType,
    electricityUsageDay,
    electricityUsageNight,
    predefinedConsumption,
    fuelType,
  });

  return { gasInput, electricityInput, paymentMethods, paymentOption };
};

const composeSubmitValues = ({
  basketId,
  energy,
  submitValues,
}: ComposeRequirementsParams): EnergyQuoteRequirementsInput => {
  const { details } = energy;

  let electricVehicleTariffExpressionOfInterest =
    details.electricVehicleTariffExpressionOfInterest;

  // Ensure to sign out from EV tariff interest if user selected gas only
  if (
    electricVehicleTariffExpressionOfInterest &&
    submitValues.fuelType === EnergyFuelType.GasOnly
  ) {
    electricVehicleTariffExpressionOfInterest = false;
  }

  const { electricityInput, gasInput, paymentOption, paymentMethods } =
    parseInput(submitValues);

  return {
    basketId,
    paymentOption,
    paymentMethods,
    gas: gasInput,
    electricity: electricityInput,
    electricVehicleTariffExpressionOfInterest,
  };
};

export { composeSubmitValues };
